import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { Currency, CurrencySymbol } from "_shared/money/currencies/currency";
import Money from "_shared/money/money";
import React from "react";
import { NumericFormat } from "react-number-format";

const CurrencyInputContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row"
}));

type CurrencyProps = {
  className?: string;
  size?: "small" | "medium";
  variant?: "standard" | "outlined" | "filled";
  value?: Money;
  label?: string;
  readOnly?: boolean;
  onChange: (currency: Currency, amount: number) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlurCapture?: React.FocusEventHandler<HTMLInputElement>;
}

export default function CurrencyInput(props: Readonly<CurrencyProps>) {
  const {
    className,
    size,
    variant,
    value, 
    label, 
    readOnly, 
    onChange,
    onKeyDown,
    onBlurCapture
  } = props;

  const [currency, setCurrency] = React.useState(value?.currency ?? Currency.USD);
  const [amount, setAmount] = React.useState<number>(value?.amount ?? 0);

  return (
    <CurrencyInputContainer className={className}>
      <NumericFormat
        prefix={CurrencySymbol(currency)}
        label={label}
        customInput={TextField}
        value={amount}
        type="text"
        size={size ?? 'medium'}
        variant={variant ?? "outlined"}
        style={{ minWidth: '66px'}}
        thousandSeparator={true}
        decimalScale={2}
        onValueChange={(values, _sourceInfo) => {
          setAmount(values.floatValue ?? 0);
          onChange(currency as Currency, values.floatValue ?? 0);
        }}
        onKeyDown={onKeyDown}
        onBlurCapture={onBlurCapture}
      />
      <Select
        value={currency}
        size={size ?? 'medium'}
        variant={variant ?? "outlined"}
        style={{ fontSize: '0.95em' }}
        onChange={(event) => {
          if (readOnly) return;
          setCurrency(
            Currency[event.target.value as keyof typeof Currency]
          );
          onChange(
            Currency[event.target.value as keyof typeof Currency],
            amount
          );
        }}
        onBlurCapture={onBlurCapture}
      >
        {Object.values(Currency).map((currencyType) => (
          <MenuItem key={currencyType} value={currencyType}>
            {currencyType} ({CurrencySymbol(currencyType)})
          </MenuItem>
        ))}
      </Select>
    </CurrencyInputContainer>
  );
}
